import { Box } from '@mui/material'
import { FA_COLOR, type EmotionKey } from '@/components/researcher/liveDemo/constants'
import { RADAR_AXES } from './emotionStats'

/**
 * Radar ("spider") chart of the emotions at one instant, drawn as a transparent
 * overlay: no card, only a soft dark glow under the web so it stays legible on
 * a white stimulus. Each vertex owns a wedge in its emotion's colour, so the
 * emotion pulling the shape out is readable at a glance.
 *
 * The radius is the square root of the share, so a 4% emotion still leaves a
 * visible bump next to a 60% one while 100% still touches the rim.
 */
export default function EmotionRadar({
  shares,
  labels,
  size,
  showValues = true,
  compact = false,
}: {
  shares: Record<EmotionKey, number> | null
  labels: Record<EmotionKey, string>
  size: number
  showValues?: boolean
  /** Smaller labels for the phone layout. */
  compact?: boolean
}) {
  const n = RADAR_AXES.length
  // Room around the web for the axis labels.
  const pad = showValues ? 58 : compact ? 34 : 44
  const w = size + pad * 2
  const c = w / 2
  const R = size / 2
  const pt = (i: number, r: number): [number, number] => {
    const a = -Math.PI / 2 + (i * 2 * Math.PI) / n
    return [c + Math.cos(a) * r, c + Math.sin(a) * r]
  }
  const ring = (k: number) => RADAR_AXES.map((_, i) => pt(i, R * k).join(',')).join(' ')
  const P = RADAR_AXES.map((k, i) => pt(i, R * Math.sqrt(shares?.[k] ?? 0)))

  return (
    <Box
      sx={{
        width: w,
        height: w,
        pointerEvents: 'none',
        borderRadius: '50%',
        background:
          'radial-gradient(circle, rgba(15,16,36,.62) 0%, rgba(15,16,36,.5) 55%, rgba(15,16,36,.28) 68%, rgba(15,16,36,0) 72%)',
      }}
    >
      <svg width={w} height={w} viewBox={`0 0 ${w} ${w}`} style={{ overflow: 'visible', display: 'block' }}>
        {[0.1, 0.25, 0.5, 1].map((k) => (
          <polygon
            key={k}
            points={ring(Math.sqrt(k))}
            fill="none"
            stroke={`rgba(255,255,255,${k === 1 ? 0.35 : 0.16})`}
            strokeWidth={1}
          />
        ))}
        {RADAR_AXES.map((k, i) => {
          const [x, y] = pt(i, R)
          return <line key={k} x1={c} y1={c} x2={x} y2={y} stroke="rgba(255,255,255,.14)" />
        })}
        {shares && (
          <>
            {/* One wedge per vertex: centre → halfway to each neighbour → vertex. */}
            {RADAR_AXES.map((k, i) => {
              const a = P[(i + n - 1) % n]
              const b = P[(i + 1) % n]
              const p = P[i]
              const m1 = [(a[0] + p[0]) / 2, (a[1] + p[1]) / 2]
              const m2 = [(b[0] + p[0]) / 2, (b[1] + p[1]) / 2]
              return (
                <polygon
                  key={k}
                  points={`${c},${c} ${m1.join(',')} ${p.join(',')} ${m2.join(',')}`}
                  fill={FA_COLOR[k]}
                  fillOpacity={0.55}
                />
              )
            })}
            <polygon
              points={P.map((p) => p.join(',')).join(' ')}
              fill="none"
              stroke="#fff"
              strokeWidth={1.6}
              strokeLinejoin="round"
              opacity={0.9}
            />
            {RADAR_AXES.map((k, i) => (
              <circle key={k} cx={P[i][0]} cy={P[i][1]} r={size > 200 ? 4.5 : 3.5} fill={FA_COLOR[k]} stroke="#fff" strokeWidth={1.5} />
            ))}
          </>
        )}
        {RADAR_AXES.map((k, i) => {
          const [x, y] = pt(i, R + 14)
          const anchor = Math.abs(x - c) < 4 ? 'middle' : x > c ? 'start' : 'end'
          return (
            <text
              key={k}
              x={x}
              y={y + (y < c - 4 ? 0 : y > c + 4 ? 9 : 4)}
              textAnchor={anchor}
              style={{ fontSize: compact ? 10 : 11.5, fontWeight: 800, fill: FA_COLOR[k], filter: 'brightness(1.25)' }}
            >
              {labels[k]}
              {showValues && shares && (
                <tspan style={{ fill: '#fff', fontWeight: 700, fontSize: 10.5 }} dx={4}>
                  {Math.round(shares[k] * 100)}%
                </tspan>
              )}
            </text>
          )
        })}
      </svg>
    </Box>
  )
}
